import jsPDF from 'jspdf';

export interface PendulumReportData {
  studentName?: string;
  readings: Array<{
    lengthCm: number;
    oscillations: number;
    totalTimeS: number;
    angleDeg: number;
    massG: number;
    periodS: number;
    periodSquaredS2: number;
    remarks?: string;
  }>;
  conclusion?: string;
}

export function generatePendulumReport({ studentName = 'Student', readings, conclusion = 'No conclusion recorded.' }: PendulumReportData): Blob {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  let y = 18;

  const write = (text: string, size = 10, isBold = false, maxWidth = 180) => {
    pdf.setFontSize(size);
    pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
    const lines = pdf.splitTextToSize(text, maxWidth) as string[];
    if (y + lines.length * size * 0.6 > pageHeight - 15) {
      pdf.addPage();
      y = 18;
    }
    pdf.text(lines, 14, y);
    y += lines.length * size * 0.6 + 3;
  };

  pdf.setFillColor(239, 246, 255);
  pdf.rect(0, 0, pageWidth, 26, 'F');
  pdf.setTextColor(17, 24, 39);
  write('Simple Pendulum Laboratory Report', 18, true, 160);
  pdf.setTextColor(55, 65, 81);
  write(`Student: ${studentName}   Date: ${new Date().toLocaleDateString()}`);
  write('Aim: Determine acceleration due to gravity g using the relationship T² = (4π²/g)L and the period of a simple pendulum.');
  write('Theory: For small angles, T = 2π√(L/g), so a graph of T² against L is linear with slope = 4π²/g.');
  write('Apparatus: retort stand, clamp, string, bob, metre rule, protractor, fiducial marker, stopwatch.');

  write('Observation table', 12, true);
  write('S/No. | L (cm) | n | t (s) | T (s) | T² (s²) | θ (°) | mass (g) | remarks');

  readings.forEach((reading, index) => {
    const row = `${index + 1} | ${reading.lengthCm.toFixed(2)} | ${reading.oscillations} | ${reading.totalTimeS.toFixed(2)} | ${reading.periodS.toFixed(3)} | ${reading.periodSquaredS2.toFixed(3)} | ${reading.angleDeg.toFixed(1)} | ${reading.massG.toFixed(0)} | ${reading.remarks || '—'}`;
    write(row, 9, false, 190);
  });

  if (!readings.length) {
    write('No readings recorded yet.');
  }

  write('Conclusion and discussion', 12, true);
  write(conclusion || 'No conclusion recorded.');
  write('Discussion points: Measure length to the centre of the bob, time from the same fiducial marker, use many oscillations to reduce reaction-time uncertainty, and keep angles small to remain within the small-angle approximation.');

  return pdf.output('blob');
}
